<template>
  <sc-render :fields="fields" :emptyMessage="$t('NO_DATA_SOURCE_EXIST')">
    <div
      class="image-collage-container"
      :class="[alignmentClass, {'editorial-height': isExperienceEditor}]"
    >
      <div v-for="image in images" :class="image.class" class="image-wrapper">
        <sc-image
          v-if="isExperienceEditor"
          class="image"
          :media="image.media"
        />
        <picture v-else>
          <img
            class="image"
            :src="image.media.value[image.ratio_key]"
            :alt="image.alt"
          />
        </picture>
      </div>
    </div>
  </sc-render>
</template>

<script>
import { Image, Text } from '@sitecore-jss/sitecore-jss-vue'
import { createNamespacedHelpers } from 'vuex'
import mapFields from '~/helpers/map-fields'
import ScRender from '../sc-render/'

const { mapState: mapJssState } = createNamespacedHelpers('jss')

export default {
  name: 'ThreeImageGallery',
  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },
  components: {
    ScImage: Image,
    ScText: Text,
    ScRender
  },
  computed: {
    ...mapJssState(['isExperienceEditor']),
    ...mapFields({
      firstImage: 'firstImage',
      firstImageValue: 'firstImage.value',
      secondImage: 'secondImage',
      secondImageValue: 'secondImage.value',
      thirdImage: 'thirdImage',
      thirdImageValue: 'secondImage.value',
      alignmentClass: 'imagesAlignment.value'
    }),
    hasImages () {
      return !!(this.firstImageValue && this.secondImageValue && this.thirdImageValue)
    },
    images () {
      if (!this.hasImages) {
        return []
      }

      const imagesKeys = [
        { field: 'firstImage', ratio: 'cloudinary_4x3_fill_crop', class: 'first' },
        { field: 'secondImage', ratio: 'cloudinary_3x4_fill_crop', class: 'second' },
        { field: 'thirdImage', ratio: 'cloudinary_3x4_fill_crop', class: 'third' }
      ]

      return imagesKeys.map(key => ({
        media: this[key.field],
        alt: this[key.field].value.alt,
        ratio_key: key.ratio,
        class: key.class
      }))
    }
  }
}
</script>

<style lang="scss" scoped>

$ee-placeholder-min-height: 200px;

.editorial-height {
  min-height: $ee-placeholder-min-height;
}

.image-collage-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  @include stack-space($space-090);

  @include tablet {
    flex-direction: row;
    padding: 0 $space-100;
    @include stack-space($space-110);
  }

  &.top {
    .first {
      margin-right: $space-050;
      margin-bottom: $space-090;
      margin-left: $space-070;

      @include tablet {
        @include reset;
        margin-top: $space-060;
      }
    }

    .second {
      margin-left: $space-110;
      margin-bottom: $space-090;

      @include tablet {
        @include reset;
        margin-top: $space-080;
      }
    }

    .third {
      margin-right: $space-080;
      margin-left: $space-050;

      @include tablet {
        @include reset;
      }
    }
  }

  &.bottom {
    align-items: flex-end;

    .first {
      margin-right: $space-070;
      margin-bottom: $space-090;
      margin-left: $space-050;

      @include tablet {
        @include reset;
        margin-bottom: $space-060;
      }
    }

    .second {
      margin-right: $space-110;
      margin-bottom: $space-090;
      margin-left: $space-020;

      @include tablet {
        @include reset;
        margin-bottom: $space-080;
      }
    }

    .third {
      margin-right: $space-050;
      margin-left: $space-080;

      @include tablet {
        @include reset;
      }
    }
  }

  .image-wrapper {
    height: auto;

    @include tablet {
      width: 30%;
    }

    ::v-deep .sc-image-wrapper img {
      width: 100%;
      height: auto;
    }
  }

  .image {
    display: block;
    width: 100%;
    height: auto;
  }
}
</style>
